/**
=========================================================
* Trust Cargo - Shipping Mark
=========================================================

* Open a shipping-mark spreadsheet, read it exactly as it is, and turn its
* rows into printable carton labels.
*
* Nothing here is attached to anything. The file is not uploaded, not stored,
* and never matched against an order, a customer or a container - it is read
* in the browser and shown as it came. That is deliberate: these sheets
* arrive from suppliers in whatever shape they arrive in, and a screen that
* insists on recognising the columns is a screen that refuses half of them.
*
* The labels come out as an A4 PDF that downloads, not as a print job. The
* print dialog made every run a trip through browser settings - margins,
* scale, "headers and footers" - and a file can be sent, kept and printed
* anywhere later. Each sheet is drawn from the page itself (html2canvas) and
* laid into the PDF (jsPDF), so the Chinese headings come out exactly as the
* screen shows them without a font having to be embedded.
*
* They are laid out on the sheet rather than one to a page. The office prints
* on plain A4 and cuts it up, so four labels to a sheet is four times fewer
* sheets for the same run. The sheet is built here as pages of a fixed grid
* rather than left to flow, because a page break through the middle of a
* label is a label nobody can use.
*/

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import XLSX from "sheetjs-style";

import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Pagination, { usePagination } from "components/Pagination";
import RowActions from "components/RowActions";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import Label from "./Label";

/** Printing a thousand pages is never what somebody meant to do. */
const MAX_LABELS = 1000;

const FIELDS_KEY = "tc_shipping_mark_fields";

/**
 * The layout the office started from. It is only a starting point - lines
 * are added, renamed, reordered and removed on the screen, and what is left
 * is remembered for next time.
 */
const DEFAULT_FIELDS = [
  { id: "f1", cn: "(产品型号)", en: "item No.:", unit: "", big: false, column: "" },
  { id: "f2", cn: "(每件数量)", en: "QntlCTN:", unit: "PCS", big: true, column: "" },
  { id: "f3", cn: "(工厂货号)", en: "SN:", unit: "", big: false, column: "" },
];

/**
 * A column for a line that has none yet, guessed from what the line is
 * called. Only ever fills a blank - a mapping somebody set by hand is never
 * overwritten by a guess.
 */
const guessColumn = (field, columns) => {
  const words = `${field.en} ${field.cn}`
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fff]+/g, " ")
    .split(" ")
    .filter((w) => w.length > 1);

  if (words.length === 0) return "";

  return (
    columns.find((c) => {
      const name = c.toLowerCase().replace(/[^a-z0-9\u4e00-\u9fff]+/g, "");
      return words.some((w) => name.includes(w.replace(/\s+/g, "")));
    }) || ""
  );
};

const readFields = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(FIELDS_KEY));
    // a stored layout with no lines would leave a blank label and no way
    // back to a working one
    return Array.isArray(raw) && raw.length ? raw : DEFAULT_FIELDS;
  } catch (e) {
    return DEFAULT_FIELDS;
  }
};

const API = "https://trust-cargo.co/anas_backend/api/v2";

/**
 * The lamp is the light-goods mark, so it is driven by the goods type rather
 * than by a switch somebody has to remember. Matched on the name because the
 * types are rows the office maintains, not a fixed list - a type added later
 * and called "Light goods 2" still gets its lamp.
 */
const isLightGoods = (name) => /light|ضوء|إضاء/i.test(String(name || ""));

/**
 * How the labels sit on the paper.
 *
 * A4 first, because that is what is in the office printer. The label stock
 * sizes are kept for the day somebody prints to the label machine, but they
 * are not what a run of forty-nine goes on.
 *
 * `scale` is how big the form is drawn in its cell. A cell about the size of
 * a 100 x 150 label is 1; a cell twice that has to be drawn twice as large,
 * or the label is three lines of small print marooned on a sheet of A4.
 */
const LAYOUTS = [
  // one to a sheet first, and the one a fresh page starts on: the office
  // prints these to hand out with the cartons, and a label that has to be
  // cut off a shared sheet before it can be used is a label somebody has to
  // do something to. Four to a sheet is still here for a long run.
  { key: "a4-1", label: "A4 - 1 per page", css: "A4", w: 210, h: 297, cols: 1, rows: 1, scale: 2 },
  {
    key: "a4-2",
    label: "A4 - 2 per page",
    css: "A4",
    w: 210,
    h: 297,
    cols: 1,
    rows: 2,
    scale: 1.35,
  },
  { key: "a4-4", label: "A4 - 4 per page", css: "A4", w: 210, h: 297, cols: 2, rows: 2, scale: 1 },
  {
    key: "100x150",
    label: "100 x 150 mm label",
    css: "100mm 150mm",
    w: 100,
    h: 150,
    cols: 1,
    rows: 1,
    scale: 1,
  },
  {
    key: "100x100",
    label: "100 x 100 mm label",
    css: "100mm 100mm",
    w: 100,
    h: 100,
    cols: 1,
    rows: 1,
    scale: 0.8,
  },
];

const perPage = (layout) => layout.cols * layout.rows;

/** How wide the preview is drawn on the screen. */
const PREVIEW_W = 300;

/** CSS millimetres are exactly 96/25.4 px, which is what makes this exact. */
const MM = 96 / 25.4;

/**
 * The sheet is drawn a hair inside the paper. A block of exactly 297mm on a
 * 297mm page rounds over the edge in some browsers, and every second sheet
 * then comes out blank.
 */
const BLEED = 0.6;

/** The labels broken into sheets, so a page break never lands mid-label. */
const paginate = (all, size) => {
  const out = [];
  for (let i = 0; i < all.length; i += size) out.push(all.slice(i, i + size));
  return out;
};

/** A cell as text, whatever the sheet had in it. */
const text = (v) => {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
};

/**
 * How many cartons a row is worth.
 *
 * Anything that is not a positive whole number counts as one label, not as
 * none: a row that exists is at least one carton, and silently printing
 * nothing for it is how a box goes missing.
 */
const cartonsIn = (row, column) => {
  if (!column) return 1;

  const n = parseInt(String(row[column]).replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

/**
 * The order code behind an item number.
 *
 * The sheets number the items within one order - AKH161-1, AKH161-2 - so the
 * code is the item number with that trailing counter taken off.
 *
 * It is only taken off when what is left still reads as a code. Plenty of
 * item numbers are themselves a letter and a number (A-100), and stripping
 * those would head every label in the run with a bare "A", which is worse
 * than no title at all.
 */
const codeOf = (value) => {
  const item = text(value).trim();
  const stem = item.replace(/[-_/\s]+\d+$/, "");

  return stem.length >= 3 ? stem : item;
};

/**
 * Which line holds the item number.
 *
 * The lines are the office's to rename and reorder, so this reads what they
 * called it rather than trusting a position, and falls back to the first
 * mapped line. SN's heading (工厂货号) is deliberately not matched - it is a
 * factory reference, not the order.
 */
const itemFieldOf = (fields) =>
  fields.find((f) => f.column && /item|model|型号/i.test(`${f.en} ${f.cn}`)) ||
  fields.find((f) => f.column) ||
  null;

function ShippingMark() {
  const file = useRef(null);

  const [name, setName] = useState("");
  // the off-screen sheets the PDF is drawn from
  const sheetsRef = useRef(null);
  // {done, total} while a PDF is being built, null otherwise
  const [exporting, setExporting] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [active, setActive] = useState(0);
  const [error, setError] = useState("");

  // which column holds the carton count, and the lines on the label
  const [cartonColumn, setCartonColumn] = useState("");
  const [fields, setFields] = useState(readFields);
  const [editing, setEditing] = useState(false);
  const [company, setCompany] = useState("阿納斯公司");
  const [layout, setLayout] = useState(LAYOUTS[0]);

  // the container types as the office actually has them
  const [types, setTypes] = useState([]);
  const [typeName, setTypeName] = useState("");

  /**
   * Read once. A failure here costs the automatic lamp and nothing else -
   * the type can still be picked by hand from the fallback list, so the
   * page keeps working with the API down.
   */
  useEffect(() => {
    fetch(`${API}/container-types`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setTypes(Array.isArray(d) && d.length ? d : []))
      .catch(() => setTypes([]));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(FIELDS_KEY, JSON.stringify(fields));
    } catch (e) {
      // private browsing or a full quota: the layout just will not be
      // remembered, which must not stop it being used now
    }
  }, [fields]);

  const setField = (id, patch) =>
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const addField = () =>
    setFields((prev) => [
      ...prev,
      { id: `f${Date.now()}`, cn: "", en: "", unit: "", big: false, column: "" },
    ]);

  const removeField = (id) => setFields((prev) => prev.filter((f) => f.id !== id));

  const moveField = (id, by) =>
    setFields((prev) => {
      const i = prev.findIndex((f) => f.id === id);
      const j = i + by;
      if (i < 0 || j < 0 || j >= prev.length) return prev;

      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  /** The lines as the label wants them, for one spreadsheet row. */
  /** The order code this row's label is headed with, or "" if unreadable. */
  const itemField = itemFieldOf(fields);
  const titleFor = (row) => (itemField ? codeOf(row[itemField.column]) : "");

  const linesFor = (row) =>
    fields.map((f) => ({
      id: f.id,
      cn: f.cn,
      en: f.en,
      unit: f.unit,
      big: f.big,
      value: f.column ? row[f.column] || "" : "",
    }));

  const typeOptions = types.length ? types.map((t) => t.name) : ["Mix goods", "Light goods"];
  const lamp = isLightGoods(typeName);

  const sheet = sheets[active] || null;
  const columns = sheet ? sheet.columns : [];
  const rows = sheet ? sheet.rows : [];

  const pager = usePagination(rows, 25);

  const open = async (e) => {
    const chosen = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!chosen) return;

    setError("");

    try {
      const data = await chosen.arrayBuffer();
      const book = XLSX.read(data, { type: "array", cellDates: true });

      /**
       * header:1 gives the rows as arrays, so a sheet with duplicate or
       * blank headings keeps every column instead of collapsing them - the
       * point is to show the file as it is.
       */
      const read = book.SheetNames.map((sheetName) => {
        const grid = XLSX.utils.sheet_to_json(book.Sheets[sheetName], {
          header: 1,
          defval: "",
          blankrows: false,
          raw: false,
        });

        if (grid.length === 0) return { name: sheetName, columns: [], rows: [] };

        const width = Math.max(...grid.map((r) => r.length));
        const head = grid[0] || [];

        // a heading of its own where the sheet gave none, so every column
        // can still be picked and read
        const cols = Array.from({ length: width }, (_, i) => {
          const label = text(head[i]).trim();
          return label || `Column ${i + 1}`;
        });

        const body = grid.slice(1).map((r) => {
          const obj = {};
          cols.forEach((c, i) => {
            obj[c] = text(r[i]);
          });
          return obj;
        });

        return { name: sheetName, columns: cols, rows: body };
      });

      setSheets(read);
      setActive(0);
      setName(chosen.name);

      const first = read[0];
      const cols = first ? first.columns : [];

      // a starting point read off the headings, so a familiar sheet needs
      // no setting up at all - and a strange one still needs three clicks
      const carton = cols.find((c) => /^ctn$|carton|كرتون|boxes/i.test(c));
      setCartonColumn(carton || "");

      setFields((prev) => prev.map((f) => (f.column ? f : { ...f, column: guessColumn(f, cols) })));
    } catch (err) {
      setError("Could not read that file - is it a .xlsx or .xls workbook?");
      setSheets([]);
    }
  };

  /**
   * One entry per carton: a row worth 100 cartons becomes 100 labels, each
   * numbered within its own row.
   */
  const labels = useMemo(() => {
    const out = [];

    rows.forEach((row, rowIndex) => {
      const total = cartonsIn(row, cartonColumn);

      for (let i = 1; i <= total; i += 1) {
        out.push({ key: `${rowIndex}-${i}`, row, index: i, total });
        if (out.length >= MAX_LABELS) return;
      }
    });

    return out;
  }, [rows, cartonColumn]);

  const capped = labels.length >= MAX_LABELS;

  /**
   * The codes this run covers, in the order they appear. Usually one - a
   * shipping mark sheet is one order - but a sheet holding two is shown as
   * holding two rather than headed with whichever came first.
   */
  const codes = useMemo(() => {
    const seen = [];

    labels.forEach((l) => {
      const code = itemField ? codeOf(l.row[itemField.column]) : "";
      if (code && !seen.includes(code)) seen.push(code);
    });

    return seen;
  }, [labels, itemField]);

  const pages = useMemo(() => paginate(labels, perPage(layout)), [labels, layout]);

  // one cell in millimetres, which is both what the sheet is drawn to and
  // what the preview has to be shaped like to be worth looking at
  const cell = {
    w: (layout.w - BLEED) / layout.cols,
    h: (layout.h - BLEED) / layout.rows,
  };

  const previewScale = PREVIEW_W / (cell.w * MM);

  /**
   * The file name: the order code when the run is one order, the workbook's
   * own name otherwise - what somebody will look for in their downloads.
   */
  const pdfName = () => {
    const base = codes.length === 1 ? codes[0] : String(name || "").replace(/\.[^.]+$/, "");
    const safe = (base || "shipping-marks").replace(/[\\/:*?"<>|]+/g, "-").trim();
    return `${safe}-shipping-marks.pdf`;
  };

  /**
   * The labels as a PDF, one page per sheet, at the layout's paper size - A4
   * unless a label-stock layout was picked.
   *
   * Each sheet is captured from the off-screen copy the preview is a picture
   * of, so the file holds exactly what the screen shows. One sheet at a
   * time rather than all at once: a thousand-label run held as a thousand
   * canvases together is how a browser tab runs out of memory.
   *
   * The two libraries are fetched on the first click, not with the page -
   * most visits to this screen never make a PDF.
   */
  const downloadPdf = async () => {
    const root = sheetsRef.current;
    const sheetEls = root ? Array.from(root.querySelectorAll(".tc-page")) : [];

    if (sheetEls.length === 0 || exporting) return;

    setError("");
    setExporting({ done: 0, total: sheetEls.length });

    try {
      const [{ jsPDF: JsPDF }, { default: html2canvas }] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);

      const size = [layout.w, layout.h];
      const orientation = layout.w > layout.h ? "landscape" : "portrait";
      const pdf = new JsPDF({ unit: "mm", format: size, orientation, compress: true });

      for (let i = 0; i < sheetEls.length; i += 1) {
        // one after another on purpose - see above
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(sheetEls[i], {
          scale: 2,
          backgroundColor: "#ffffff",
          logging: false,
          scrollX: 0,
          scrollY: 0,
        });

        if (i > 0) pdf.addPage(size, orientation);

        // drawn a hair inside the page, the same BLEED the sheet is built with
        pdf.addImage(
          canvas.toDataURL("image/jpeg", 0.92),
          "JPEG",
          BLEED / 2,
          BLEED / 2,
          layout.w - BLEED,
          layout.h - BLEED
        );

        setExporting({ done: i + 1, total: sheetEls.length });
      }

      pdf.save(pdfName());
    } catch (err) {
      setError(`Could not build the PDF - ${err?.message || "unknown error"}`);
    }

    setExporting(null);
  };

  const cellStyle = { fontSize: 13.5, whiteSpace: "nowrap" };

  return (
    <>
      <DashboardLayout>
        {/* The sheets the PDF is drawn from, laid out at their real size in
          millimetres - a fixed grid of cells, a label filling each one.
          Off the side of the window rather than display:none: a hidden
          element has no size, and html2canvas captures it as a blank page. */}
        <style>{`
        #tc-labels {
          position: fixed;
          top: 0;
          left: -10000px;
          background: #fff;
          pointer-events: none;
        }
        #tc-labels .tc-page {
          box-sizing: border-box;
          width: ${layout.w - BLEED}mm;
          height: ${layout.h - BLEED}mm;
          display: grid;
          grid-template-columns: repeat(${layout.cols}, 1fr);
          grid-template-rows: repeat(${layout.rows}, 1fr);
          overflow: hidden;
          background: #fff;
        }
        #tc-labels .tc-cell {
          box-sizing: border-box;
          overflow: hidden;
          /* the cut line, only where a sheet holds more than one label -
             one to a page is cut at the page edge */
          ${perPage(layout) > 1 ? "border: 0.4pt solid #000;" : ""}
        }
      `}</style>

        <SuiBox py={1}>
          <SuiBox mb={3}>
            <Card>
              <SuiBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  px: 3,
                  py: 2.5,
                }}
              >
                <SuiBox sx={{ flex: "1 1 auto", minWidth: 0 }}>
                  <SuiTypography variant="h6">
                    Shipping Mark
                    {codes.length > 0 && (
                      <SuiTypography
                        component="span"
                        variant="h6"
                        sx={({ palette }) => ({ color: palette.primary.main, ml: 1 })}
                      >
                        {codes.length === 1 ? codes[0] : `${codes[0]} +${codes.length - 1} more`}
                      </SuiTypography>
                    )}
                  </SuiTypography>
                  <SuiTypography sx={{ fontSize: 13, opacity: 0.7 }}>
                    {name
                      ? `${name} - read in this browser only, nothing is uploaded`
                      : "Open a shipping-mark workbook to read it and download carton labels as a PDF"}
                  </SuiTypography>
                </SuiBox>

                <input
                  ref={file}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={open}
                  style={{ display: "none" }}
                />

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => file.current && file.current.click()}
                  startIcon={<Icon>upload_file</Icon>}
                >
                  {name ? "Open another" : "Open file"}
                </Button>

                {labels.length > 0 && (
                  <Button
                    variant="outlined"
                    color="dark"
                    onClick={downloadPdf}
                    disabled={Boolean(exporting)}
                    startIcon={<Icon>picture_as_pdf</Icon>}
                  >
                    {exporting
                      ? `Building PDF ${exporting.done}/${exporting.total}`
                      : `Download PDF - ${labels.length} label${labels.length === 1 ? "" : "s"}`}
                  </Button>
                )}
              </SuiBox>

              {error && (
                <SuiBox px={3} pb={2}>
                  <Alert severity="error">{error}</Alert>
                </SuiBox>
              )}

              {sheets.length > 1 && (
                <SuiBox px={3} pb={2} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {sheets.map((s, i) => (
                    <Chip
                      key={s.name}
                      size="small"
                      label={`${s.name} (${s.rows.length})`}
                      onClick={() => setActive(i)}
                      variant={i === active ? "filled" : "outlined"}
                      sx={{ fontWeight: i === active ? 700 : 500, cursor: "pointer" }}
                    />
                  ))}
                </SuiBox>
              )}

              {sheet && columns.length > 0 && (
                <SuiBox px={3} pb={2}>
                  <SuiBox
                    sx={({ palette, borders, functions: { rgba } }) => ({
                      p: 2,
                      borderRadius: borders.borderRadius.lg,
                      border: `1px solid ${rgba(palette.dark.main, 0.12)}`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    })}
                  >
                    {/* the four settings, one per cell, all the same width.
                        As a wrapping flex row they came out ragged - a button
                        and a chip in among the inputs, labels wider than the
                        boxes holding them, and every field a different size.
                        A grid gives each one a column and drops them into
                        neat rows as the window narrows. */}
                    <SuiBox
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                        alignItems: "start",
                      }}
                    >
                      <TextField
                        select
                        label="Cartons per row"
                        value={cartonColumn}
                        onChange={(e) => setCartonColumn(e.target.value)}
                        SelectProps={{ native: true }}
                        helperText="One label per row when not set"
                      >
                        <option value="">One label per row</option>
                        {columns.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </TextField>

                      <TextField
                        select
                        label="Goods type"
                        value={typeName}
                        onChange={(e) => setTypeName(e.target.value)}
                        SelectProps={{ native: true }}
                        helperText={lamp ? "Lamp shown on the label" : "No lamp"}
                      >
                        <option value="">Not set</option>
                        {typeOptions.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </TextField>

                      <TextField
                        select
                        label="Sheet layout"
                        value={layout.key}
                        onChange={(e) =>
                          setLayout(LAYOUTS.find((z) => z.key === e.target.value) || LAYOUTS[0])
                        }
                        SelectProps={{ native: true }}
                        helperText={`${pages.length} sheet${
                          pages.length === 1 ? "" : "s"
                        } to print`}
                      >
                        {LAYOUTS.map((z) => (
                          <option key={z.key} value={z.key}>
                            {z.label}
                          </option>
                        ))}
                      </TextField>

                      {/* the label read "Company (foot of the label)" and was
                          cut off mid-word by the box it sat on - the same
                          thing is said in the helper text, which has room */}
                      <TextField
                        label="Company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        helperText="Printed at the foot of every label"
                      />
                    </SuiBox>

                    {/* what you DO with the settings, kept off the line where
                        you SET them - a button in among the inputs is what
                        made the row look thrown together */}
                    <SuiBox
                      sx={({ palette, functions: { rgba } }) => ({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        pt: 1.5,
                        borderTop: `1px solid ${rgba(palette.dark.main, 0.08)}`,
                      })}
                    >
                      <Button
                        variant={editing ? "contained" : "outlined"}
                        color="dark"
                        onClick={() => setEditing((v) => !v)}
                        startIcon={<Icon>tune</Icon>}
                      >
                        {`Fields (${fields.length})`}
                      </Button>

                      <Chip
                        label={`${labels.length} label${labels.length === 1 ? "" : "s"}`}
                        sx={({ palette, functions: { rgba } }) => ({
                          fontWeight: 700,
                          fontSize: 14,
                          height: 34,
                          px: 0.5,
                          backgroundColor: rgba(palette.primary.main, 0.16),
                        })}
                      />
                    </SuiBox>

                    {editing && (
                      <SuiBox
                        sx={({ palette, borders, functions: { rgba } }) => ({
                          p: 1.5,
                          borderRadius: borders.borderRadius.md,
                          backgroundColor: rgba(palette.dark.main, 0.04),
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        })}
                      >
                        <SuiTypography sx={{ fontSize: 12.5, fontWeight: 700, opacity: 0.75 }}>
                          Lines on the label, in the order they print
                        </SuiTypography>

                        {fields.map((f, i) => (
                          <SuiBox
                            key={f.id}
                            sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
                          >
                            <TextField
                              size="small"
                              label="Heading"
                              value={f.cn}
                              onChange={(e) => setField(f.id, { cn: e.target.value })}
                              InputLabelProps={{ shrink: true }}
                              placeholder="(产品型号)"
                              sx={{ width: 150 }}
                            />
                            <TextField
                              size="small"
                              label="Name"
                              value={f.en}
                              onChange={(e) => setField(f.id, { en: e.target.value })}
                              InputLabelProps={{ shrink: true }}
                              placeholder="item No.:"
                              sx={{ width: 140 }}
                            />
                            <TextField
                              select
                              size="small"
                              label="Column"
                              value={f.column}
                              onChange={(e) => setField(f.id, { column: e.target.value })}
                              SelectProps={{ native: true }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 170 }}
                            >
                              <option value="">Blank</option>
                              {columns.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </TextField>
                            <TextField
                              size="small"
                              label="Unit"
                              value={f.unit}
                              onChange={(e) => setField(f.id, { unit: e.target.value })}
                              InputLabelProps={{ shrink: true }}
                              placeholder="PCS"
                              sx={{ width: 90 }}
                            />

                            <Chip
                              size="small"
                              label="Big"
                              onClick={() => setField(f.id, { big: !f.big })}
                              variant={f.big ? "filled" : "outlined"}
                              sx={{ cursor: "pointer", fontWeight: f.big ? 700 : 500 }}
                            />

                            <SuiBox sx={{ display: "flex", gap: 0.25 }}>
                              <RowActions
                                actions={[
                                  {
                                    icon: "arrow_upward",
                                    label: "Move up",
                                    disabled: i === 0,
                                    onClick: () => moveField(f.id, -1),
                                  },
                                  {
                                    icon: "arrow_downward",
                                    label: "Move down",
                                    disabled: i === fields.length - 1,
                                    onClick: () => moveField(f.id, 1),
                                  },
                                  {
                                    icon: "delete",
                                    label: "Remove this line",
                                    tone: "danger",
                                    // the last line has to stay, or the label
                                    // has nothing on it and no way back
                                    disabled: fields.length === 1,
                                    onClick: () => removeField(f.id),
                                  },
                                ]}
                              />
                            </SuiBox>
                          </SuiBox>
                        ))}

                        <SuiBox sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" onClick={addField} startIcon={<Icon>add</Icon>}>
                            Add a line
                          </Button>
                          <Button
                            size="small"
                            color="dark"
                            onClick={() => setFields(DEFAULT_FIELDS)}
                          >
                            Reset to the standard label
                          </Button>
                        </SuiBox>
                      </SuiBox>
                    )}

                    {/* the first label, exactly as it will print - a preview
                      is the only way to catch a column mapped to the wrong
                      line before a hundred sheets come out.

                      It is the real label shrunk, not a smaller drawing of
                      one: the cell is laid out at its true size in
                      millimetres and scaled down as a whole, so what is on
                      the screen is what comes off the printer, down to how
                      much room the company name has left. */}
                    {labels.length > 0 && (
                      <SuiBox>
                        <SuiTypography
                          sx={{ fontSize: 12.5, fontWeight: 700, opacity: 0.75, mb: 0.5 }}
                        >
                          {`First label - ${cell.w.toFixed(0)} x ${cell.h.toFixed(0)} mm`}
                        </SuiTypography>
                        <SuiBox
                          sx={({ palette, functions: { rgba } }) => ({
                            width: PREVIEW_W,
                            height: Math.round((PREVIEW_W * cell.h) / cell.w),
                            border: `1px solid ${rgba(palette.dark.main, 0.55)}`,
                            overflow: "hidden",
                            backgroundColor: "#fff",
                          })}
                        >
                          <SuiBox
                            sx={{
                              width: `${cell.w}mm`,
                              height: `${cell.h}mm`,
                              transform: `scale(${previewScale})`,
                              transformOrigin: "top left",
                            }}
                          >
                            <Label
                              lines={linesFor(labels[0].row)}
                              title={titleFor(labels[0].row)}
                              company={company}
                              lamp={lamp}
                              scale={layout.scale}
                            />
                          </SuiBox>
                        </SuiBox>
                      </SuiBox>
                    )}

                    {capped && (
                      <Alert severity="warning">
                        {`Stopped at ${MAX_LABELS} labels. The sheet asks for more than that - check the
                        cartons column before printing.`}
                      </Alert>
                    )}
                  </SuiBox>
                </SuiBox>
              )}

              {sheet && (
                <>
                  <SuiBox sx={{ overflowX: "auto", px: 1 }}>
                    <Table aria-label="shipping mark">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={{ ...cellStyle, opacity: 0.5 }}>
                            #
                          </TableCell>
                          {columns.map((c) => (
                            <TableCell
                              key={c}
                              align="center"
                              sx={{ ...cellStyle, fontWeight: 700 }}
                            >
                              {c}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pager.pageRows.map((row, i) => (
                          // the sheet's own order is the only identity a raw row
                          // has - two rows can be identical and both are real
                          // eslint-disable-next-line react/no-array-index-key
                          <TableRow key={`r${pager.page}-${i}`}>
                            <TableCell align="center" sx={{ ...cellStyle, opacity: 0.5 }}>
                              {(pager.page - 1) * pager.pageSize + i + 1}
                            </TableCell>
                            {columns.map((c) => (
                              <TableCell key={c} align="center" sx={cellStyle}>
                                {row[c] === "" ? "-" : row[c]}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}

                        {rows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                              <SuiTypography variant="button" color="text">
                                This sheet is empty
                              </SuiTypography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </SuiBox>
                  <Pagination {...pager} />
                </>
              )}

              {!sheet && !error && (
                <SuiBox sx={{ py: 8, textAlign: "center", opacity: 0.6 }}>
                  <Icon sx={{ fontSize: "44px !important" }}>description</Icon>
                  <SuiTypography sx={{ fontSize: 14, mt: 1 }}>No file open</SuiTypography>
                </SuiBox>
              )}
            </Card>
          </SuiBox>
        </SuiBox>

        <Footer />
      </DashboardLayout>

      {/* The sheets, at the end of the document rather than inside the page,
          so nothing the labels would otherwise sit inside - a scrolled card,
          a transformed preview - can shift or scale them in the PDF. */}
      {createPortal(
        <div id="tc-labels" ref={sheetsRef}>
          {pages.map((page, i) => (
            // the sheets have no identity beyond their order
            // eslint-disable-next-line react/no-array-index-key
            <div className="tc-page" key={`sheet-${i}`}>
              {page.map((l) => (
                <div className="tc-cell" key={l.key}>
                  <Label
                    lines={linesFor(l.row)}
                    title={titleFor(l.row)}
                    company={company}
                    lamp={lamp}
                    scale={layout.scale}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

export default ShippingMark;
